import { createSelector } from "reselect";
import get from "lodash/get";
import camelCase from "lodash/camelCase";
import isEmpty from "lodash/isEmpty";
import { localeSelector } from "./locale";

export const liquidOcrDataDomain = state =>
  get(state.api, "liquidOcrData.data", {});

const kycDomain = state => get(state, "api.kyc.data", {});

const kycConfigDomain = state => get(state, "api.kycConfig.data", {});

const nationalityDomain = state => get(state, "api.nationalityList.data", []);

// ***************************************************************
// ************************ kyc Selectors ************************
// ***************************************************************

export const liquidOcrDataSelector = createSelector(
  liquidOcrDataDomain,
  liquidOcrData => liquidOcrData
);

export const kycSelector = createSelector(kycDomain, kyc => kyc);

export const kycConfigSelector = createSelector(kycConfigDomain, config => {
  return !isEmpty(config.documents)
    ? config.documents.reduce((prev, current) => {
        return {
          ...prev,
          [camelCase(current.type.toLowerCase())]: {
            ...current
          }
        };
      }, {})
    : {};
});

export const nationalitySelector = createSelector(
  nationalityDomain,
  localeSelector,
  (nationalities, locale) =>
    nationalities.map(nationality => ({
      label: get(locale, nationality, nationality),
      value: nationality
    }))
);

export const kycDocTypesSelector = createSelector(kycConfigSelector, config =>
  get(config, "documentTypes", [])
);

export const kycOneOffDocTypesSelector = createSelector(
  kycConfigSelector,
  config => get(config, "oneOffDocTypes", [])
);

export const kycMandatoryDocTypesSelector = createSelector(
  kycConfigSelector,
  config => get(config, "mandatoryDocTypes", [])
);

export const kycDocumentsTypesSelector = createSelector(
  kycConfigSelector,
  localeSelector,
  (config, locale) => {
    return Object.keys(config).map(doc => ({
      label: get(locale, doc, doc),
      value: doc
    }));
  }
);

export const kycDocumentsProofsSelector = createSelector(
  kycConfigSelector,
  config => {
    const docs = Object.keys(config).reduce((prev, curr) => {
      const field = config[curr];
      return [
        ...prev,
        {
          document: {
            attachments: [
              get(field, "frontPage.enabled", false) && "frontPage",
              get(field, "backPage.enabled", false) && "backPage"
            ].filter(Boolean),
            formats: [
              ...new Set(
                get(field, "backPage.formats"),
                get(field, "frontPage.formats")
              )
            ],
            maxSize:
              get(field, "frontPage.maxSize") ||
              get(field, "backPage.maxSize") ||
              0
          },
          type: curr
        }
      ];
    }, []);

    const selfie = Object.keys(config).find(key =>
      get(config[key], "selfieImage.enabled")
    );

    if (selfie) {
      docs.push({
        document: {
          attachments: ["selfieImage"],
          formats: get(config[selfie], "selfieImage.formats"),
          maxSize: get(config[selfie], "selfieImage.maxSize")
        },
        type: "selfie"
      });
    }

    return docs;
  }
);

export const isDocumentImagesNeededSelector = createSelector(
  kycConfigSelector,
  config => {
    return Object.keys(config).reduce(
      (prev, val) => ({
        ...prev,
        [val]:
          get(config[val], "frontPage.enabled") ||
          get(config[val], "backPage.enabled")
      }),
      {}
    );
  }
);

// ***************************************************************
// ************************ kyc makeSelect ***********************
// ***************************************************************

export const makeSelectLiquidOcrData = () => liquidOcrDataSelector;
export const makeSelectKycData = () => kycSelector;
export const makeSelectKycConfig = () => kycConfigSelector;
export const makeSelectKycOneOffDocTypes = () => kycOneOffDocTypesSelector;
export const makeSelectKycMandatoryDocTypes = () =>
  kycMandatoryDocTypesSelector;
export const makeSelectKycDocumentsTypes = () => kycDocumentsTypesSelector;
export const makeSelectKycDocumentProofs = () => kycDocumentsProofsSelector;
export const makeSelectKycAllOffDocTypes = () => kycDocTypesSelector;
export const makeSelectNationalityList = () => nationalitySelector;
export const makeSelectIsDocumentImagesNeeded = () =>
  isDocumentImagesNeededSelector;
